<template>
    <vx-box class="flex flex-col h-full">
        <div
            class="flex flex-row pr-4 pl-4 sm:pl-6 py-4 items-start gap-2 justify-between sm:items-center border-b border-solid border-t-0 border-x-0 border-slate-100">
            <div class="flex flex-col gap-1">
                <div class="text-sm text-slate-500">
                    To:
                    <span class="text-slate-800"
                        data-test="contact-title">
                        {{ contactTitle }}
                    </span>
                </div>
                <div data-test="scheduled-date" class="text-base leading-none font-bold text-slate-800">
                    <vx-popover hover
                        :arrow="true"
                        placement="top">
                        {{ fromNow(model.send_at) }}
                        <template v-slot:content>
                            {{ shortDateTimeTzSpread(model.send_at) }}
                        </template>
                    </vx-popover>
                </div>
            </div>
            <div v-if="!model.enqueued_at">
                <vx-dropdown placement="right">
                    <vx-button data-test="dropdown"
                        type="button"
                        color="muted"
                        outline
                        class="w-[45px]">
                        <font-awesome-icon widthAuto :icon="faEllipsis" />
                    </vx-button>

                    <template v-slot:items>
                        <vx-dropdown-item data-test="edit-button"
                            @click="editModal = true">
                            Edit
                            <font-awesome-icon widthAuto :icon="faFilePen"></font-awesome-icon>
                        </vx-dropdown-item>
                        <vx-dropdown-item data-test="delete-button"
                            @click="deleteScheduledMessage()">
                            Delete
                            <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                        </vx-dropdown-item>
                    </template>
                </vx-dropdown>
            </div>
        </div>

        <div class="flex flex-col gap-4 p-4 sm:p-6 relative grow justify-between">
            <div class="text-sm flex flex-col gap-2 md:gap-1">

                <div data-test="user-name">
                    <span class="w-24 inline-block mr-2 text-slate-500">Scheduled By: </span>
                    <span class="text-slate-800">
                        {{ model.user?.name || '-' }}
                    </span>
                </div>
                <div>
                    <span class="w-24 inline-block mr-2 text-slate-500">Created At: </span>
                    <span class="text-slate-800">
                        {{ toShortDateTime(model.created_at) }}
                    </span>
                </div>
            </div>

            <message-cloud data-test="message-cloud"
                fullWidth
                :message="model"
                :showDetails="false"
                @mediaPlaceholderClick="mediaExpiredModal = $event.isFileExpired"></message-cloud>

        </div>
        <expired-media-modal v-model="mediaExpiredModal"></expired-media-modal>
        <scheduled-message-modal :message="model"
            :contact="props.contact"
            v-model:visible="editModal"
            @scheduled="model = $event"></scheduled-message-modal>
    </vx-box>
</template>

<script setup>
import { VxBox, VxPopover, VxDropdown, VxDropdownItem, VxButton, VxSwal } from '@voxie/frontend-components';
import { ref , computed } from 'vue';
import { fromNow, shortDateTimeTzSpread, toShortDateTime } from '~/utils/date';
import { displayContactName } from '../filters';
import ExpiredMediaModal from '~/components/modals/ExpiredMediaModal.vue';
import MessageCloud from '~/components/messages/browser-extension/components/misc/MessageCloud.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faEllipsis, faFilePen, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { scheduledMessagesClient } from '~/services';
import { throwSwalRequestError } from '~/utils/request';
import ScheduledMessageModal from '~/components/scheduled-messages/modals/ScheduledMessageModal.vue';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';

const model = defineModel({ type: Object, required: true });
const props = defineProps({
    contact: {
        type: Object,
        required: false,
    }
});

const editModal = ref(false);
const emit = defineEmits(['deleted']);

const contactTitle = computed(() => displayContactName(props.contact));

const mediaExpiredModal = ref(false);

const deleting = ref(false);
const deleteScheduledMessage = async () => {
    deleting.value = true;
    VxSwal.confirmation(
        'Delete Scheduled Message',
        'Are you sure you want to delete this scheduled message?',
        'danger',
        () =>
            scheduledMessagesClient
                .deleteScheduledMessage({ teamId: model.value.team_id, groupId: model.value.group_id }, model.value.contact_id, model.value.id)
                .then(() => emit('deleted', { ...model.value }))
                .catch((err) => {
                    if (err?.response?.status === HTTP_NOT_FOUND) {
                        emit('deleted', { ...model.value })
                        return;
                    }

                    return throwSwalRequestError(err)
                })
    ).finally(() => deleting.value = false);
};

defineExpose({
    editModal,
    deleting,
});
</script>
